// Pure builders for the tender/split fields of saveOrderPos. Ported from the Android POS:
//  - Reductions (gift card / loyalty / store credit) reduce the grand total and are recorded as
//    order-level fields (is_gift_card / is_loyality / is_store_credit + giftcard_json + lp_spend_* +
//    sc_spend_amount). The SERVER debits the real balances from these values verbatim.
//  - Payment tenders (cash / card / EBT) pay the remaining balance. One tender = one-shot order;
//    2+ tenders = split order with splitpay_json (is_split_payment "1").
// Field names verified against Process_order.php (_processGiftCards / _processLoyaltyPoints /
// _processSplitPayments).

import { roundHalfUp, toNum } from "./money";

const money = (v) => roundHalfUp(v).toFixed(2);
const S = (v) => (v == null ? "" : String(v));

/**
 * is_X flag (matches OrderCreateViewModel.isGiftCard): "0" absent, "2" when this tender ALONE
 * zeroes the remaining owes (no other reduction present), else "1" (partial or combined).
 */
export function tenderFlag(amt, remainingOwes, otherA, otherB) {
  if (toNum(amt) <= 0) return "0";
  if (toNum(remainingOwes) <= 0 && toNum(otherA) <= 0 && toNum(otherB) <= 0) return "2";
  return "1";
}

/**
 * giftcard_json — exact port of OrderCreateViewModel.giftCardJson(orderId):
 *  - ISSUED cards: every cart line named "Gift Card(NUMBER)" → gift_type "credit" (server creates or
 *    loads the card with that balance), amount = the line's unit price.
 *  - REDEEMED card(s): gift_type "debit" (server subtracts from balance).
 *  - gift_desc = the ORDER ID (Android puts orderId there); amounts are JSON numbers.
 * Empty state is "" (not "[]").
 */
export function buildGiftcardJson(giftCards, cart = [], orderId = "") {
  const arr = [];
  for (const l of cart) {
    const name = String(l.orderName || "");
    if (!name.includes("Gift Card")) continue;
    const a = name.indexOf("(");
    const b = name.indexOf(")");
    if (a < 0 || b <= a + 1) continue;
    arr.push({
      gift_amount: roundHalfUp(toNum(l.orderUnitPrice)),
      gift_type: "credit",
      gift_desc: S(orderId),
      gift_number: name.slice(a + 1, b).trim(),
    });
  }
  for (const g of giftCards || []) {
    if (toNum(g.amountUsed) <= 0) continue;
    arr.push({
      gift_amount: roundHalfUp(toNum(g.amountUsed)),
      gift_type: "debit",
      gift_desc: S(orderId),
      gift_number: S(g.number).trim(),
    });
  }
  return arr.length ? JSON.stringify(arr) : "";
}

/**
 * pay_type for a splitpay_json row (mirrors the Android split JSON):
 * cash → "cash"; credit/debit → "card"; EBT (food/cash) → "credit".
 */
export function splitPayType(payType) {
  const p = String(payType || "").toLowerCase();
  if (p === "cash") return "cash";
  if (p === "foodebt" || p === "cashebt" || p === "ebt") return "credit"; // Android coerces EBT → credit
  if (p === "debit") return "debit";
  return "credit"; // credit (Android emits cash/credit/debit — never "card")
}

/**
 * Build splitpay_json from ordered payment tenders. remaining_amount is the cumulative balance
 * after each tender (starts at the reduced grand total). Empty state is "".
 * @param {Array} tenders [{ payType, amount, tip, paymentId, cardType, last4, paxDetails }]
 * @param {number} startingOwes grand total after reductions (what the tenders collectively pay)
 * @param {string} createdDateTime
 */
export function buildSplitpayJson(tenders, startingOwes, createdDateTime) {
  let remaining = toNum(startingOwes);
  const rows = (tenders || []).map((t, i) => {
    const amt = toNum(t.amount);
    remaining = roundHalfUp(remaining - amt);
    return {
      pay_count: String(i + 1),
      pay_type: splitPayType(t.payType),
      // Android stores the GROSS amount charged to the tender = base split + tip + surcharge.
      // (remaining_amount below still decrements by the BASE only — see remaining = remaining - amt.)
      pay_amount: money(amt + toNum(t.tip) + toNum(t.ncaAmount)),
      tip: money(toNum(t.tip)),
      remaining_amount: money(Math.max(remaining, 0)),
      cash_discounting_amount: toNum(t.ncaAmount) > 0 ? money(toNum(t.ncaAmount)) : "0.00",
      cash_discounting_percentage: t.ncaPercent != null ? S(t.ncaPercent) : "0.00",
      cash_back_amt: toNum(t.cashbackAmount) > 0 ? money(toNum(t.cashbackAmount)) : "0.00",
      cash_back_fee: toNum(t.cashbackFee) > 0 ? money(toNum(t.cashbackFee)) : "0.00",
      payment_id: S(t.paymentId), // row carries the tender's real id ("" for cash); the "Cash" literal is top-level only
      last_four_digit: S(t.last4),
      pax_details: S(t.paxDetails),
      card_type: S(t.cardType),
      created_date_time: S(createdDateTime),
    };
  });
  return rows.length ? JSON.stringify(rows) : "";
}
